import type { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  inject,
  input,
  model,
  NO_ERRORS_SCHEMA,
  type OnInit,
  type SimpleChanges,
  signal,
  ViewChild,
  type WritableSignal,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Routes } from '@core/routes/routes';
import { ButtonModule } from '@fino-ui/button';
import { FinoCommonModule } from '@fino-ui/common';
import { IbanPipe } from '@fino-ui/finances';
import { ibanValidator, SubmittableForm } from '@fino-ui/forms';
import { FinoIconModule } from '@fino-ui/icon';
import { FinoInputModule } from '@fino-ui/input';
import { NotificationService } from '@fino-ui/notification';
import { FinoTranslationModule, TranslateService } from '@fino-ui/translation';
import { isEmpty, map, merge } from 'lodash';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { ClipboardModule } from 'ngx-clipboard';
import { LocalStorage } from 'ngx-webstorage';
import { Subject, Subscription } from 'rxjs';
import { AppService } from '../../app.service';
import { IbanTypeaheadComponent } from '../../components/iban-typeahead/iban-typeahead.component';
import { NavbarService } from '../../components/navbar/navbar.service';
import { TenantInfosComponent } from '../../components/tenant-infos/tenant-infos.component';
import { IbanColorPipe } from '../../core/pipes/iban-color.pipe';
import {
  DOCUMENT_TYPES_BUTTONS,
  REJECTION_REASONS,
} from './identify-document.constants';
import {
  IdentifyDocumentService,
  type PadContact,
} from './identify-document.service';
import { RejectionReasonComponent } from './rejection-reason/rejection-reason.component';
import { SiblingsComponent } from './siblings/siblings.component';

const IBAN_VALIDATORS = [Validators.required, ibanValidator];

@Component({
  selector: 'app-identify-document',
  templateUrl: './identify-document.component.html',
  styleUrls: ['./identify-document.component.sass'],
  imports: [
    PdfJsViewerModule,
    FormsModule,
    ReactiveFormsModule,
    SiblingsComponent,
    FinoTranslationModule,
    FinoIconModule,
    FinoCommonModule,
    ClipboardModule,
    ButtonModule,
    IbanTypeaheadComponent,
    FinoInputModule,
    TenantInfosComponent,
    IbanPipe,
    IbanColorPipe,
    RejectionReasonComponent,
  ],
  schemas: [NO_ERRORS_SCHEMA],
})
export class IdentifyDocumentComponent implements OnInit {
  private readonly identifyDocumentService = inject(IdentifyDocumentService);
  private readonly route = inject(ActivatedRoute);
  private readonly appService = inject(AppService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);
  private readonly navbarService = inject(NavbarService);

  public readonly pdfUrl = signal('');
  public readonly job = signal<any>(null);
  public readonly noJobLeft = signal(false);
  public form: SubmittableForm = new SubmittableForm({
    tenant: new FormControl('', Validators.required),
    documentType: new FormControl('', Validators.required),
    oldIban: new FormControl('', IBAN_VALIDATORS),
    newIban: new FormControl('', IBAN_VALIDATORS),
    rejectionReason: new FormControl('', Validators.required),
    rejectionReasonText: new FormControl(''),
  });
  public loadNextDocumentForm: SubmittableForm = new SubmittableForm({
    loadNext: new FormControl(true),
  });

  public readonly disableTypeahead: WritableSignal<boolean> = signal(false);

  @LocalStorage()
  public pagemode: any;

  public readonly isClarification = input(false);

  public readonly dontRenderNextJob = model(false);

  public readonly assignedJob = input<any>();

  public readonly jobId = input<string>('');

  public readonly previousJobId = input<string>('');

  public submitSubscription: Subscription = Subscription.EMPTY;
  public readonly tenantInfos = signal<PadContact | null>(null);
  public readonly sidebarInfos = signal<any>(null);
  public readonly showDuplicateListLink = signal(false);
  public readonly duplicateId = signal('');
  public readonly isUpgrightDisplay = signal(false);

  public readonly newBankCustomerName = signal('');
  public readonly oldBankCustomerName = signal('');

  @ViewChild('pdfViewer') pdfViewer: any;

  public ibanTypeaheadOpenEvents = {
    newIban: new Subject<any>(),
    oldIban: new Subject<any>(),
  };

  public readonly currentPage = signal(0);

  public readonly documentTypeItems = signal<any>([]);

  get rejectionReasons() {
    return REJECTION_REASONS;
  }

  get courierBaseUrl() {
    return window.location.origin;
  }

  public readonly ibansOnCurrentPage = computed(
    () =>
      this.job()?.classificationByStephan?.suggestedIBANsPerPage[
        this.currentPage()
      ],
  );

  get canSwitchIbans() {
    const { oldIban, newIban } = this.form.value;
    return !isEmpty(oldIban) || !isEmpty(newIban);
  }

  switchIbanValues() {
    const { oldIban, newIban } = this.form.value;
    this.form.patchValue({
      oldIban: newIban,
      newIban: oldIban,
    });
  }

  renderNextJob() {
    this.route.params.subscribe((r) => {
      if (r.id) {
        this.router.navigate([Routes.IdentifyDocument]);
      } else {
        this.renderJob();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const { assignedJob } = changes;
    if (assignedJob) {
      this.setModel(assignedJob.currentValue);
    }
  }

  public readonly hasClosedStatus = computed(
    () => this.job()?.status === 'closed',
  );

  ngOnInit() {
    this.route.params.subscribe((r) => {
      this.renderJob(r.id);
    });

    this.navbarService.toggleUpright.subscribe((toggleUpright) => {
      this.isUpgrightDisplay.set(toggleUpright);
    });

    this.loadNextDocumentForm.valueChanges.subscribe((r) => {
      const { loadNext } = r;
      this.dontRenderNextJob.set(!loadNext);
    });

    this.documentTypeItems.set(this.identifyDocumentService.documentTypes);

    this.form
      .get('rejectionReason')
      ?.valueChanges.subscribe((rejectionReason) => {
        this.setIbansAsRequired(rejectionReason === 'OK');
      });
  }

  openConfirmSendToBankModal() {
    this.identifyDocumentService
      .openConfirmSendToBankModal()
      .subscribe((approved) => {
        if (approved) this.submitJob();
      });
  }

  pdfViewerLoaded(_$event: any) {
    this.pdfViewer.PDFViewerApplication.eventBus.on(
      'sidebarviewchanged',
      ($event: any) => {
        this.pagemode = $event.source.isOpen ? 'thumbs' : 'none';
      },
    );
  }

  public pdfViewerPageChange(count: number) {
    this.currentPage.set(count - 1);
  }

  get documentTypeForm() {
    return this.form.get('documentType') || new FormControl();
  }

  public readonly tenant = computed(() => this.job()?.envelope.tenant);

  public readonly hasReviewReason = computed(() => this.job()?.reviewReason);

  selectDocumentType(documentType: string) {
    this.disableTypeahead.set(documentType === 'WARRANT');
    const a = this.disableTypeahead();
    this.form.patchValue({ documentType });
  }

  get documentType() {
    return this.form.value.documentType;
  }

  get documentTypeError() {
    return this.form.touched && this.form.controls.documentType?.errors;
  }

  maybeSubmitJob() {
    const { rejectionReason } = this.form.controls;
    if (rejectionReason.value === this.rejectionReasons.NEEDS_CLARIFICATION) {
      return this.openConfirmSendToBankModal();
    }
    this.submitJob();
  }

  deleteJobDone() {
    if (this.dontRenderNextJob()) {
      this.noJobLeft.set(true);
      return;
    }
    this.renderNextJob();
  }

  submitJob() {
    this.form.markAllAsTouched();

    const documntTypes = map(DOCUMENT_TYPES_BUTTONS, (i) => i.type);
    const selectedDocumentType = this.form.controls.documentType?.value;
    if (!documntTypes.includes(selectedDocumentType)) {
      this.form.controls.documentType?.setErrors({ required: true });
    }
    if (this.form.valid) {
      this.submitSubscription = this.identifyDocumentService
        .submitJob(this.job().id, this.form.value)
        .subscribe({
          next: () => {
            if (this.dontRenderNextJob()) {
              this.noJobLeft.set(true);
              return;
            }
            this.renderNextJob();
          },
          error: (error: HttpErrorResponse) => {
            if (error.status === 409) {
              this.showDuplicateListLink.set(true);
              this.duplicateId.set(error.error?.duplicateId);
              return this.notificationService.error(
                this.translateService.instant('error.409'),
              );
            }
            if (
              error.status === 404 &&
              error.error?.errorMessage === 'NO_JOB_FOUND'
            ) {
              return this.notificationService.error(
                this.translateService.instant('error.no_job_found'),
              );
            }
            this.notificationService.error(
              error.error?.message || error.message,
            );
          },
        });
    }
  }

  loadDuplicateList() {
    window.open(`/jobs/captureTransaction/${this.duplicateId()}`, '_blank');
  }

  public readonly customerDiffers = computed(
    () =>
      !isEmpty(this.oldBankCustomerName()) &&
      !isEmpty(this.newBankCustomerName()) &&
      this.oldBankCustomerName() !== this.newBankCustomerName(),
  );

  ibanSelected($event: any) {
    const type: string = $event.type;

    if (type === 'newIban') {
      this.newBankCustomerName.set($event.customer);
    } else if (type === 'oldIban') {
      this.oldBankCustomerName.set($event.customer);
    }

    if ($event.showCounterResults) {
      const counterType = type === 'newIban' ? 'oldIban' : 'newIban';
      (this.ibanTypeaheadOpenEvents as any)[counterType].next($event);
    }
  }

  setIbansAsRequired(yes = true) {
    this.form.get('newIban')?.setValidators(yes ? IBAN_VALIDATORS : []);
    this.form.get('newIban')?.updateValueAndValidity();
    this.form.get('oldIban')?.setValidators(yes ? IBAN_VALIDATORS : []);
    this.form.get('oldIban')?.updateValueAndValidity();
  }

  setModel(job: any) {
    this.navbarService.loadCounts.next('identifyDocument');
    this.noJobLeft.set(!job);

    if (this.noJobLeft()) {
      return;
    }
    this.job.set(job);
    this.appService.currentTicketId.set(job.id);
    this.form.reset();

    this.newBankCustomerName.set('');
    this.oldBankCustomerName.set('');

    this.identifyDocumentService.queryBLZ(job.envelope.tenant).subscribe({
      next: (res) => {
        this.tenantInfos.set(res);
        this.sidebarInfos.set(merge(res, job));
      },
      error: (_res) => {
        // 404 tenant not found
        this.sidebarInfos.set(job);
      },
    });

    const {
      newIban,
      oldIban,
      documentType,
      rejectionReason,
      rejectionReasonText,
      tenant,
    } = job.envelope;

    this.form.patchValue({
      newIban,
      oldIban,
      tenant,
      rejectionReason: rejectionReason ?? this.rejectionReasons.OK,
      rejectionReasonText,
      documentType: documentType ?? job.suggestedType,
    });

    this.setIbansAsRequired(true);

    this.pdfUrl.set('');
    setTimeout(() => {
      const url = new URL(job.docUrl);
      this.pdfUrl.set(escape(url.pathname + url.search + url.hash));
    }, 100);
  }

  renderJob(id: string = '') {
    if (this.assignedJob()) {
      return;
    }
    this.identifyDocumentService
      .fetchNextJob(id)
      .subscribe((result: any) => this.setModel(result));
  }
}
